import { getCounty } from "@nwga/contracts/region";
import { useTRPC } from "@nwga/features/client";
import { JobCard } from "@nwga/features/jobs/ui";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@nwga/ui/components/avatar";
import { Button } from "@nwga/ui/components/button";
import { EmptyState } from "@nwga/ui/components/empty-state";
import { Separator } from "@nwga/ui/components/separator";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeftIcon,
	BriefcaseIcon,
	ExternalLinkIcon,
	MapPinIcon,
} from "lucide-react";
import { Container } from "../../components/container.tsx";
import { ErrorState } from "../../components/error-state.tsx";

/**
 * An employer's public page.
 *
 * The only public page a business has now that the directory is gone — it is
 * reached from a listing, from search, and from the link a business shares
 * itself. Its job is to say who this employer is and what they are hiring for;
 * there is no browse-all-employers view above it, so it stands alone and has to
 * offer its own way back to the board.
 */
export const Route = createFileRoute("/businesses/$slug")({
	component: BusinessPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(
			context.trpc.businesses.bySlug.queryOptions(params),
		),
});

function BusinessPage() {
	const { slug } = Route.useParams();
	const trpc = useTRPC();

	const business = useQuery(trpc.businesses.bySlug.queryOptions({ slug }));

	// Deliberately a second query rather than an embedded list: the listings have
	// their own cache lifetime and are invalidated by their own events.
	const jobs = useQuery({
		...trpc.jobs.search.queryOptions({
			businessId: business.data?.id ?? "",
			limit: 50,
			sort: "published_at",
			order: "desc",
		}),
		enabled: business.isSuccess,
	});

	if (business.isPending) {
		return (
			<Container size="narrow" className="space-y-4 py-10" aria-busy="true">
				<Skeleton className="h-12 w-1/2" />
				<Skeleton className="h-20 w-full" />
			</Container>
		);
	}

	if (business.isError) {
		return (
			<Container size="narrow" className="py-10">
				<ErrorState
					error={business.error}
					onRetry={() => void business.refetch()}
				/>
			</Container>
		);
	}

	const county = getCounty(business.data.county);
	const openings = jobs.data?.items ?? [];

	return (
		<Container size="narrow" className="py-10">
			<Button asChild variant="ghost" size="sm" className="-ml-2 mb-6">
				<Link to="/jobs">
					<ArrowLeftIcon className="size-4" aria-hidden="true" />
					All jobs
				</Link>
			</Button>

			<header className="flex flex-wrap items-start gap-4">
				<Avatar className="size-14 rounded-lg">
					{business.data.logoUrl !== null && (
						<AvatarImage src={business.data.logoUrl} alt="" />
					)}
					<AvatarFallback className="rounded-lg text-lg">
						{business.data.name.slice(0, 2).toUpperCase()}
					</AvatarFallback>
				</Avatar>

				<div className="min-w-0 space-y-1.5">
					<h1 className="font-heading font-bold text-2xl tracking-tight text-balance sm:text-3xl">
						{business.data.name}
					</h1>

					<p className="flex items-center gap-1.5 text-muted-foreground text-sm">
						<MapPinIcon className="size-4 shrink-0" aria-hidden="true" />
						{business.data.city ?? county?.seat} · {county?.name} County, GA
					</p>

					{business.data.website !== null && (
						<a
							href={business.data.website}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1.5 text-sm underline-offset-4 hover:underline"
						>
							{business.data.website.replace(/^https?:\/\//, "")}
							<ExternalLinkIcon
								className="size-3.5 text-brand"
								aria-hidden="true"
							/>
							<span className="sr-only">(opens in new tab)</span>
						</a>
					)}
				</div>
			</header>

			{business.data.description !== null && (
				<p className="mt-6 whitespace-pre-wrap text-pretty leading-relaxed">
					{business.data.description}
				</p>
			)}

			<Separator className="my-8" />

			<h2 className="font-medium text-lg">
				Open roles
				{openings.length > 0 && (
					<span className="ml-2 text-muted-foreground text-sm tabular-nums">
						{openings.length}
					</span>
				)}
			</h2>

			<div className="mt-4 space-y-3">
				{jobs.isPending && <Skeleton className="h-24 w-full" />}

				{jobs.isSuccess && openings.length === 0 && (
					<EmptyState
						icon={BriefcaseIcon}
						title={`No open roles at ${business.data.name} right now`}
						description="New listings appear here as soon as they are posted."
						action={
							<Button asChild variant="outline">
								<Link to="/jobs">Browse every opening</Link>
							</Button>
						}
					/>
				)}

				{openings.map((job) => (
					<JobCard key={job.id} job={job} />
				))}
			</div>
		</Container>
	);
}
